import './layer-item.less';
import classnames from 'classnames';
import { cloneDeep } from 'lodash';
import React, { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Popover, Tooltip } from '@meitu/saas-ui';
import QrCodeLayerContent from '@meitu/saas-editor-core/es/layers/qr-code-layer/views/layer-content';
import ImageContent from '@meitu/saas-editor-core/es/layers/vector-layer/views/image-content';
import { base64Kit } from '@meitu/saas-editor-core/es/utils/base64-kit';
import { getSysType, SYSTEM_TYPE } from '@meitu/saas-editor-core/es/utils/platform-kit';
import { traverseTree } from '@meitu/saas-editor-core/es/utils/tree-kit';
import {
    IBrushLayerProps,
    IImageLayerProps,
    ILayerProps,
    ITableLayerProps,
    ITextLayerProps,
    LayerType,
} from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { useTypo } from '@/hooks/use-typo';
import { editor } from '@/instance';
import { useStoreState } from '@/store';
import { extractEnglishWords } from '@/utils';
import { imgModeMap, reverseLayers } from './utils';

const system = getSysType();
const prefix = system === SYSTEM_TYPE.MAC ? 'mac' : 'win';

const keys = [
    {
        name: '组合',
        win: 'Ctrl + G',
        mac: '⌘ + G',
        key: 'makeGroup',
    },
].map((el) => {
    return {
        ...el,
        keyName: el[prefix],
    };
});

const ContextMenu: React.FC<{ handleClick: Function }> = ({ handleClick }) => {
    return (
        <ul className="layer-context-menu">
            {keys.map((item, index) => {
                return (
                    <li className="layer-context-menu__item" key={index} onClick={() => handleClick(item.key)}>
                        <div className="l-content-menu__item__title">
                            <span>{item.name}</span>
                        </div>
                        <span>{item.keyName}</span>
                    </li>
                );
            })}
        </ul>
    );
};

const LayerIcon: React.FC<{ url: string }> = React.memo(({ url }) => {
    const [imgSrc, setImgSrc] = useState('');
    useEffect(() => {
        const renderUrl = base64Kit.getLocalUrl(url);
        setImgSrc(renderUrl);
    }, [url]);
    return imgSrc ? <img src={imgSrc} style={{ width: 16, height: 16, objectFit: 'contain' }} alt="" /> : null;
});

const LayerItem: React.FC<{
    layer: ILayerProps;
    setDisableOperate: (disable: boolean) => void;
    parentIsVisible: boolean;
    selectIds: string[];
    selectLayer: Function;
    handleMakeGroup: Function;
    activeLayerId: string | null;
}> = ({ layer, setDisableOperate, parentIsVisible, selectIds, selectLayer, handleMakeGroup, activeLayerId }) => {
    const { layerType, title: layerTitle } = layer;
    let iconElem;
    let title = layerTitle;
    const [currentEditTitle, setCurrentEditTitle] = useState('');
    const { check } = useTypo();
    const [isTypo, setIsTypo] = useState(false);
    const [errorWords, setErrorWords] = useState<string[]>([]);
    const isShowTypo = useStoreState((state) => state.editorModel.isShowTypo);
    useEffect(() => {
        if (layerType === LayerType.TEXT_LAYER && (layer.data as ITextLayerProps).text && isShowTypo) {
            const text = (layer.data as ITextLayerProps).text;
            // 提取英文，并且不包含标点符号
            const enText = extractEnglishWords(text);
            const errorWordList: string[] = [];
            if (enText.length > 0) {
                enText.forEach((word) => {
                    const result = check(word);
                    if (!result) {
                        errorWordList.push(word);
                    }
                });
            }
            setErrorWords(errorWordList);
        }
    }, [layer, isShowTypo]);

    useEffect(() => {
        setIsTypo(errorWords.length > 0);
        // console.log('errorWords==', errorWords);
    }, [errorWords]);

    switch (layerType) {
        case LayerType.BACKGROUND_LAYER: {
            title = layer?.title || '背景';
            iconElem = '';
            break;
        }
        case LayerType.TEXT_LAYER: {
            const { text } = layer.data as ITextLayerProps;
            title = title || text;
            iconElem = <IconFont name="TextSquare" fontSize={16} />;
            break;
        }

        case LayerType.IMAGE_LAYER: {
            const { url } = layer.data as IImageLayerProps;
            title = layer?.title || '图案';
            iconElem = <LayerIcon url={url} />;
            break;
        }
        case LayerType.BRUSH_LAYER: {
            const { screenshotUrl: brushScreenshotUrl } = layer.data as IBrushLayerProps;
            title = layer?.title || '形状';
            iconElem = brushScreenshotUrl ? (
                <LayerIcon url={brushScreenshotUrl} />
            ) : (
                <IconFont name="BrushAndBoard" fontSize={16} />
            );
            break;
        }
        case LayerType.TABLE: {
            const { screenshotUrl: tableScreenshotUrl } = layer.data as ITableLayerProps;
            title = layer?.title || '表格';
            iconElem = tableScreenshotUrl ? (
                <LayerIcon url={tableScreenshotUrl} />
            ) : (
                <IconFont name="TabulationBold" fontSize={16} />
            );
            break;
        }
        case LayerType.GROUP_LAYER: {
            title = layer?.title || '图层分组';
            iconElem = (
                <div className="c-layer-list-item-icon-group">
                    <IconFont
                        name={layer.extraInfo?.isExpanded ? 'ChevronDownBold' : 'ChevronRightBold'}
                        fontSize={12}
                        onClick={() => {
                            if (!layer.extraInfo) {
                                layer.extraInfo = {};
                            }
                            layer.extraInfo.isExpanded = !layer.extraInfo.isExpanded;
                            editor.stage.updateLayer(reverseLayers([layer])[0]);
                        }}
                    />
                    <IconFont name="Folder" fontSize={16} />
                </div>
            );
            break;
        }
        case LayerType.VECTOR_LAYER: {
            title = `${layer?.title || '形状'}`;
            iconElem = (
                <div
                    style={{
                        transform: `scale(${Math.min(16 / layer.width, 16 / layer.height)})`,
                        transformOrigin: 'top left',
                    }}
                >
                    <ImageContent layer={{ ...layer, id: uuid() }} identity={''} isNavigator={false} />
                </div>
            );
            break;
        }
        case LayerType.QR_CODE: {
            title = `${layer?.title || '二维码'}`;
            iconElem = (
                <div
                    style={{
                        transform: `scale(${Math.min(14 / layer.width, 14 / layer.height)})`,
                        transformOrigin: 'top left',
                    }}
                >
                    <QrCodeLayerContent layer={{ ...layer, id: uuid() }} />
                </div>
            );
            break;
        }

        case LayerType.CHART: {
            title = layer?.title || '图表';
            iconElem = <IconFont name="StackedColumnGraph" fontSize={16} />;
            break;
        }

        case LayerType.MARKUP_LAYER: {
            title = layer?.title || '标注笔';
            iconElem = <IconFont name="RegionVary" fontSize={16} />;
            break;
        }

        case LayerType.MEDIA_LAYER: {
            title = layer?.title || '动态素材';
            iconElem = <IconFont name="LivePhotoPlay" fontSize={16} />;
            break;
        }
    }

    const handleClick = (key: string) => {
        switch (key) {
            case 'makeGroup':
                handleMakeGroup();
                break;
        }
    };

    return (
        <Popover
            content={selectIds.includes(layer.id) ? <ContextMenu handleClick={handleClick} /> : null}
            trigger="contextMenu"
            placement="rightTop"
            overlayClassName="layer-context-menu-popover "
        >
            <div
                className={classnames('c-layer-list-item', {
                    active: layer.id === activeLayerId && selectIds.includes(layer.id),
                    'temp-active': selectIds.includes(layer.id) && layer.id !== activeLayerId,
                })}
                onClick={() => {
                    // 编辑器的子图层组是无法选中的
                    if (layer.hidden || !parentIsVisible) {
                        return;
                    }
                    selectLayer(layer);
                    if (layer.layerType === LayerType.GROUP_LAYER && layer.parentId) {
                        return;
                    }
                    editor.active.set(layer.id);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Delete') {
                        editor.layer.delete(layer.id);
                    }
                }}
            >
                <div className="c-layer-list-item__left">
                    <span
                        className="c-layer-list-item-icon"
                        style={{
                            height: '16px',
                            width: layer.layerType === LayerType.GROUP_LAYER ? '40px' : '16px',
                        }}
                    >
                        {iconElem}
                    </span>
                    {layer.extraInfo?.isEditTitle ? (
                        <input
                            type="text"
                            value={currentEditTitle}
                            className="c-layer-list-item-title-input"
                            onChange={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setCurrentEditTitle(e.target.value);
                            }}
                            onBlur={() => {
                                layer.title = currentEditTitle;
                                editor.imageLayer.updateLayer(
                                    cloneDeep(
                                        reverseLayers([
                                            {
                                                ...layer,
                                                extraInfo: { ...(layer.extraInfo || {}), isEditTitle: false },
                                            },
                                        ])[0],
                                    ),
                                    cloneDeep(reverseLayers([layer])[0]),
                                    true,
                                );
                                editor.active.set(layer.id);
                                setDisableOperate(false);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    layer.title = currentEditTitle;
                                    editor.imageLayer.updateLayer(
                                        cloneDeep(
                                            reverseLayers([
                                                {
                                                    ...layer,
                                                    extraInfo: { ...(layer.extraInfo || {}), isEditTitle: false },
                                                },
                                            ])[0],
                                        ),
                                        cloneDeep(reverseLayers([layer])[0]),
                                        true,
                                    );
                                    setDisableOperate(false);
                                    editor.active.set(layer.id);
                                }
                            }}
                        />
                    ) : (
                        <span
                            className="c-layer-list-item-title"
                            onDoubleClick={() => {
                                if (!layer.extraInfo) {
                                    layer.extraInfo = {};
                                }
                                layer.extraInfo.isEditTitle = true;
                                setCurrentEditTitle(layerTitle);
                                editor.stage.updateLayer(cloneDeep(reverseLayers([layer])[0]));
                                setDisableOperate(true);
                            }}
                        >
                            {layer.mode ? (
                                <span style={{ color: 'red' }}>{imgModeMap[layer.mode]?.slice(2)} </span>
                            ) : null}
                            {`${title}${layer.layerType === LayerType.VECTOR_LAYER || layer.layerType === LayerType.BRUSH_LAYER ? ' (svg)' : ''}`}
                        </span>
                    )}
                </div>
                <div className="c-layer-list-item__right">
                    {isTypo && isShowTypo ? (
                        <Tooltip title={`存在可能拼写错误：【 ${errorWords} 】请检查`}>
                            <IconFont name="InfoCircleFill" fontSize={12} color="#ff9800" />
                        </Tooltip>
                    ) : null}
                    {layer.lock ? <IconFont name="LockFill" fontSize={12} color="#ff2200" /> : null}
                    {parentIsVisible ? (
                        <IconFont
                            name={layer.hidden ? 'InvisibleBold' : 'VisibleBold'}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const oldLayer = cloneDeep(reverseLayers([layer])[0]);
                                traverseTree(layer, (child: ILayerProps) => {
                                    child.hidden = !oldLayer.hidden;
                                });
                                editor.imageLayer.updateLayer(cloneDeep(reverseLayers([layer])[0]), oldLayer, true);
                                editor.active.delete();
                            }}
                            fontSize={12}
                        />
                    ) : null}
                </div>
            </div>
        </Popover>
    );
};

export default LayerItem;
